import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Info, ChevronDown, ChevronRight, Copy, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function OcrPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [settings, setSettings] = useState({ auto_classify: true, confidence_threshold: 0.95 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promptData, setPromptData] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ocr-settings")
      .then((r) => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadPrompt = async () => {
    if (promptData) { setPromptOpen((o) => !o); return; }
    setPromptLoading(true);
    try {
      const r = await api.get("/ocr-settings/prompt-preview");
      setPromptData(r.data);
      setPromptOpen(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPromptLoading(false);
    }
  };

  const copyPrompt = () => {
    if (!promptData) return;
    const text = `=== Instruction système ===\n${promptData.system_message}\n\n=== Prompt utilisateur ===\n${promptData.user_prompt}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ocr-settings", settings);
      toast.success("Paramètres OCR enregistrés.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const pct = Math.round(settings.confidence_threshold * 100);

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-zinc-500 py-8">
      <Loader2 size={16} className="animate-spin" />Chargement…
    </div>
  );

  return (
    <div className="max-w-2xl space-y-8">
      <div className="flex items-start gap-3 p-4 bg-[#002FA7]/5 border border-[#002FA7]/15">
        <Sparkles size={16} strokeWidth={1.5} className="text-[#002FA7] mt-0.5 shrink-0" />
        <p className="text-sm text-zinc-700 leading-relaxed">
          À chaque dépôt dans le SAS (upload ou email entrant), Gemini analyse
          automatiquement le PDF pour identifier son type et extraire ses métadonnées.
          Si la confiance dépasse le seuil configuré, le document est classé
          sans intervention manuelle.
        </p>
      </div>

      {/* Toggle auto-classify */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono mb-3">
          Classement automatique
        </div>
        <button
          data-testid="ocr-auto-classify-toggle"
          disabled={!isAdmin}
          onClick={() => isAdmin && setSettings((s) => ({ ...s, auto_classify: !s.auto_classify }))}
          className={`relative inline-flex h-6 w-11 items-center transition-colors focus:outline-none
            ${settings.auto_classify ? "bg-[#002FA7]" : "bg-zinc-300"}
            ${!isAdmin ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
          <span className={`inline-block h-4 w-4 transform bg-white shadow transition-transform
            ${settings.auto_classify ? "translate-x-6" : "translate-x-1"}`} />
        </button>
        <span className="ml-3 text-sm text-zinc-700">
          {settings.auto_classify ? "Activé" : "Désactivé"}
        </span>
        <p className="mt-1 text-xs text-zinc-400">
          {settings.auto_classify
            ? "Les documents dont la confiance dépasse le seuil seront classés automatiquement."
            : "Les documents restent dans la file et l'IA pré-remplit seulement les champs."}
        </p>
      </div>

      {/* Threshold slider */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
            Seuil de confiance
          </div>
          <span data-testid="ocr-threshold-value"
            className="text-2xl font-mono font-bold text-[#002FA7]">
            {pct} %
          </span>
        </div>
        <input
          data-testid="ocr-threshold-slider"
          type="range" min={50} max={99} step={1} value={pct}
          disabled={!isAdmin}
          onChange={(e) => isAdmin && setSettings((s) => ({ ...s, confidence_threshold: Number(e.target.value) / 100 }))}
          className="w-full accent-[#002FA7] disabled:opacity-60 disabled:cursor-not-allowed"
        />
        <div className="flex justify-between text-[10px] font-mono text-zinc-400 mt-1">
          <span>50 %</span><span>99 %</span>
        </div>
        <div className="mt-3 flex items-start gap-2 text-xs text-zinc-500">
          <Info size={12} strokeWidth={1.5} className="mt-0.5 shrink-0" />
          <span>
            En dessous du seuil, le document reste dans la file avec les champs
            pré-remplis. En dessus, il est classé directement et une notification
            in-app est envoyée aux administrateurs.
          </span>
        </div>
      </div>

      {isAdmin && (
        <button data-testid="ocr-save-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-6 py-2.5 transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      )}

      {/* Section prompt */}
      <div className="border border-zinc-200">
        <button
          data-testid="ocr-prompt-toggle"
          onClick={loadPrompt}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
        >
          <span className="flex items-center gap-2">
            {promptLoading
              ? <Loader2 size={14} className="animate-spin text-zinc-400" />
              : promptOpen
              ? <ChevronDown size={14} className="text-zinc-400" />
              : <ChevronRight size={14} className="text-zinc-400" />}
            Prompt envoyé à Gemini
            {promptData && (
              <span className="text-[10px] font-mono text-zinc-400">
                ({promptData.document_types_count} type{promptData.document_types_count !== 1 ? "s" : ""} actif{promptData.document_types_count !== 1 ? "s" : ""})
              </span>
            )}
          </span>
          {promptData && promptOpen && (
            <button
              onClick={(e) => { e.stopPropagation(); copyPrompt(); }}
              className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400 hover:text-zinc-700 px-2 py-0.5 border border-zinc-200 hover:border-zinc-400 transition-colors"
            >
              {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
              {copied ? "Copié" : "Copier"}
            </button>
          )}
        </button>

        {promptOpen && promptData && (
          <div className="border-t border-zinc-200">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
              <span className="text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-400">
                Instruction système
              </span>
            </div>
            <pre className="px-4 py-3 text-[11px] font-mono text-zinc-600 whitespace-pre-wrap leading-relaxed bg-zinc-50/50 max-h-32 overflow-y-auto">
              {promptData.system_message}
            </pre>
            <div className="px-4 py-2 bg-zinc-50 border-y border-zinc-200">
              <span className="text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-400">
                Prompt utilisateur (envoyé avec le PDF)
              </span>
            </div>
            <pre className="px-4 py-3 text-[11px] font-mono text-zinc-600 whitespace-pre-wrap leading-relaxed max-h-[480px] overflow-y-auto">
              {promptData.user_prompt}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
